import React, {useState} from 'react';
import Modal from '../../../../Presentation/components/ui/Modal';
import AppInput from '../../../../Presentation/components/ui/AppInput';
import Button from '../../../../Presentation/components/ui/Button';
import {DashboardUser, UpdateUserPasswordRequest} from '../../models/user.model';

interface ResetUserPasswordModalProps {
  open: boolean;
  user: DashboardUser | null;
  onClose: () => void;
  onSubmit: (data: UpdateUserPasswordRequest) => Promise<void>;
}

/**
 * Admin-initiated password reset for another user. The admin sets a new
 * password directly (no current password) via PUT /users/:id/password.
 */
const ResetUserPasswordModal: React.FC<ResetUserPasswordModalProps> = ({
  open,
  user,
  onClose,
  onSubmit
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
    setServerError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    if (loading) {return;}
    reset();
    onClose();
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!newPassword.trim()) {
      next.newPassword = 'كلمة المرور الجديدة مطلوبة';
    } else if (newPassword.length < 8) {
      next.newPassword = 'كلمة المرور يجب أن تكون 8 أحرف على الأقل';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      next.newPassword = 'كلمة المرور يجب أن تحتوي على حرف صغير وكبير ورقم';
    }
    if (confirmPassword !== newPassword) {
      next.confirmPassword = 'كلمة المرور وتأكيدها غير متطابقين';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    if (!user || !validate()) {return;}

    setLoading(true);
    try {
      await onSubmit({id: user.id, newPassword});
      setSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'فشل إعادة تعيين كلمة المرور');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="إعادة تعيين كلمة المرور">
      <form onSubmit={handleSubmit} className="space-y-4">
        {user && (
          <div className="bg-brand-50 p-4 rounded-lg text-sm text-brand-700 text-right">
            <p><span className="font-medium">المستخدم:</span> {user.fullName || user.username}</p>
            <p><span className="font-medium">اسم المستخدم:</span> {user.username}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm text-right">
            تم إعادة تعيين كلمة المرور بنجاح. أبلغ المستخدم بكلمة المرور الجديدة.
          </div>
        )}
        {serverError && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm text-right">
            {serverError}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
            كلمة المرور الجديدة *
          </label>
          <AppInput
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="أدخل كلمة المرور الجديدة"
            error={errors.newPassword}
            className="w-full"
          />
          <p className="text-xs text-gray-500 mt-1 text-right">
            8 أحرف على الأقل، وتحتوي على حرف صغير وكبير ورقم
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
            تأكيد كلمة المرور الجديدة *
          </label>
          <AppInput
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="أعد إدخال كلمة المرور الجديدة"
            error={errors.confirmPassword}
            className="w-full"
          />
        </div>

        <div className="flex gap-4 justify-end pt-4 border-t">
          <Button type="button" variant="outline" onClick={handleClose} disabled={loading} className="px-6">
            {success ? 'إغلاق' : 'إلغاء'}
          </Button>
          <Button type="submit" variant="primary" isLoading={loading} disabled={loading} className="px-6">
            {loading ? 'جاري الحفظ...' : 'إعادة تعيين'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ResetUserPasswordModal;
